/**
 * Recherche-Tiefe-Picker (off / normal / deep) — EIN gemeinsames Idiom für
 * Chat-Composer (AskComposer/UnifiedComposer) und Präsentations-Wizard.
 *
 * Semantik: der Picker reicht die STUFE `research_mode ∈ {off, normal, deep}`
 * ans Backend; dort mappt app/clients/perplexity.py::resolve_research_model auf
 * das Perplexity-Modell (off -> kein Call, normal -> sonar, deep -> sonar-pro).
 *
 * Ästhetik: ruhiges Segmented-Control (dezent, keine Motion, AA-Kontrast).
 * Die aktive Stufe ist klar erkennbar (gefüllt); off/normal/deep eindeutig
 * beschriftet. Der Chat-Composer merkt die Wahl in localStorage `sb_research_mode`.
 */
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";

export type ResearchMode = "off" | "normal" | "deep";

export const RESEARCH_MODE_KEY = "sb_research_mode";
export const DEFAULT_RESEARCH_MODE: ResearchMode = "normal";

export const RESEARCH_MODES: { id: ResearchMode; label: string; hint: string }[] = [
  { id: "off", label: "Aus", hint: "Keine Web-Recherche — nur Projekt-Wissen" },
  { id: "normal", label: "Normal", hint: "Web-Recherche (Sonar) — schnell, für die meisten Fälle" },
  { id: "deep", label: "Tief", hint: "Web-Recherche (Sonar Pro) — gründlicher, langsamer" },
];

/** Stufe-State mit localStorage-Persistenz (`sb_research_mode`), Default „Normal". */
export function useResearchMode(): [ResearchMode, (m: ResearchMode) => void] {
  const [mode, setMode] = useState<ResearchMode>(() => {
    const v = localStorage.getItem(RESEARCH_MODE_KEY) as ResearchMode | null;
    return v && RESEARCH_MODES.some((m) => m.id === v) ? v : DEFAULT_RESEARCH_MODE;
  });
  useEffect(() => {
    localStorage.setItem(RESEARCH_MODE_KEY, mode);
  }, [mode]);
  return [mode, setMode];
}

export interface ResearchModePickerProps {
  mode: ResearchMode;
  setMode: (m: ResearchMode) => void;
  /** Kompakt (Composer-Zeile) vs. voll (Wizard mit Label + Hint). */
  variant?: "inline" | "full";
  /** Deaktiviert (z. B. Perplexity nicht konfiguriert). */
  disabled?: boolean;
  className?: string;
}

/**
 * Segmented-Control. `inline` = kleine Chip-Reihe für die Composer-Toolbar;
 * `full` = mit Icon-Label „Recherche" + Hint-Zeile für den Wizard.
 */
export default function ResearchModePicker({
  mode,
  setMode,
  variant = "inline",
  disabled = false,
  className = "",
}: ResearchModePickerProps) {
  const active = RESEARCH_MODES.find((m) => m.id === mode) ?? RESEARCH_MODES[1];

  const segmented = (
    <div
      role="radiogroup"
      aria-label="Recherche-Tiefe"
      className={
        "inline-flex items-center rounded-full border border-border bg-surface-2 p-0.5 " +
        (disabled ? "opacity-50 " : "")
      }
    >
      {RESEARCH_MODES.map((m) => {
        const isActive = mode === m.id;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={disabled}
            onClick={() => !disabled && setMode(m.id)}
            title={m.hint}
            className={
              "px-2.5 py-1 rounded-full text-xs transition-colors " +
              (disabled ? "cursor-not-allowed " : "") +
              (isActive
                ? "bg-accent-500 text-white font-medium"
                : "text-text-2 hover:text-text")
            }
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className={"inline-flex items-center gap-1.5 " + className}>
        <Globe className="w-3.5 h-3.5 text-text-3 shrink-0" aria-hidden />
        {segmented}
      </div>
    );
  }

  // full: Label + Segmented + Hint (Wizard).
  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-medium text-text inline-flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5 text-text-3" aria-hidden /> Web-Recherche
        </label>
        {segmented}
      </div>
      <div className="mt-1 text-[11px] text-text-3 leading-snug">
        {disabled
          ? "Perplexity ist nicht konfiguriert — Web-Recherche steht nicht zur Verfügung."
          : active.hint}
      </div>
    </div>
  );
}
